import React, { useEffect, useRef, useState } from "react";

import { ReactComponent as AnthropicIcon } from "../../images/providers/anthropic.svg";
import { ReactComponent as MoonshotIcon } from "../../images/providers/moonshot.svg";
import { ReactComponent as OpenAIIcon } from "../../images/providers/openai.svg";
import { ReactComponent as XIcon } from "../../images/providers/x.svg";
import { authService } from "../../services/auth";

const DEFAULT_SUBTITLE = "Balanced set of models optimized for cost and speed";

// Brand tiles + Simple Icons SVGs (openai/anthropic/x/moonshot).
const PROVIDER_STYLES = {
  preset: { bg: "#1f2937", fg: "#ffffff", Icon: null, letter: "P" },
  anthropic: { bg: "#d4a27f", fg: "#1a120b", Icon: AnthropicIcon },
  openai: { bg: "#10a37f", fg: "#ffffff", Icon: OpenAIIcon },
  "x-ai": { bg: "#000000", fg: "#ffffff", Icon: XIcon },
  "z-ai": { bg: "#3b82f6", fg: "#ffffff", Icon: null, letter: "Z" },
  moonshot: { bg: "#6366f1", fg: "#ffffff", Icon: MoonshotIcon },
};

const providerStyle = (provider) =>
  PROVIDER_STYLES[provider] || { bg: "#6b7280", fg: "#ffffff", Icon: null, letter: "?" };

const ProviderMark = ({ provider, compact = false }) => {
  const style = providerStyle(provider);
  const box = compact ? "h-5 w-5" : "h-8 w-8";
  const iconClass = compact ? "h-3 w-3" : "h-4 w-4";
  const letter = compact ? "text-[10px]" : "text-xs";
  const Icon = style.Icon;

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-lg ${box}`}
      style={{ backgroundColor: style.bg, color: style.fg }}
      aria-hidden
    >
      {Icon ? (
        <Icon className={iconClass} />
      ) : (
        <span className={`font-bold ${letter}`}>{style.letter || "?"}</span>
      )}
    </span>
  );
};

const PremiumModelPicker = ({ value, onChange, compact = false }) => {
  const [models, setModels] = useState([]);
  const [canChooseModel, setCanChooseModel] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    authService.fetchUser((response) => {
      const canChoose = Boolean(response.data?.features?.can_choose_model);
      setModels(Array.isArray(response.data?.env?.premium_models) ? response.data.env.premium_models : []);
      setCanChooseModel(canChoose);
      // Free users keep Default only; drop any inherited project override.
      if (!canChoose && value) onChange("");
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const selected = models.find((model) => model.id === value);

  const pick = (id) => {
    if (id && !canChooseModel) return;
    onChange(id);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={compact
          ? "inline-flex max-w-[14rem] items-center gap-2 rounded-full border border-theme-secondary px-2.5 py-1.5 text-left text-xs font-medium transition hover:bg-theme-secondary"
          : "flex w-full items-center gap-3 rounded-xl border border-theme-secondary px-3 py-3 text-left transition hover:opacity-90"}
      >
        {selected ? (
          <ProviderMark provider={selected.provider} compact={compact} />
        ) : (
          <span
            className={`inline-flex shrink-0 items-center justify-center rounded-lg bg-theme-secondary font-bold opacity-70 ${
              compact ? "h-5 w-5 text-[10px]" : "h-8 w-8 text-xs"
            }`}
            aria-hidden
          >
            D
          </span>
        )}
        {compact ? (
          <span className="truncate">{selected ? selected.label : "Default model"}</span>
        ) : (
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">
            {selected ? selected.label : "Default"}
          </span>
          <span className="mt-0.5 block text-xs opacity-60">
            {selected
              ? selected.description || selected.provider
              : DEFAULT_SUBTITLE}
          </span>
        </span>
        )}
        <svg
          className={`h-4 w-4 shrink-0 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className={`absolute left-0 top-full z-50 mt-2 max-h-72 overflow-y-auto rounded-xl border border-theme-secondary bg-theme-main py-1 shadow-lg ${
            compact ? "w-[36rem]" : "right-0"
          }`}
        >
          <button
            type="button"
            role="option"
            aria-selected={!value}
            onClick={() => pick("")}
            className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition hover:bg-black hover:bg-opacity-5 ${
              !value ? "bg-black bg-opacity-5" : ""
            }`}
          >
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-theme-secondary text-xs font-bold opacity-70">
              D
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold">Default</span>
              <span className="mt-0.5 block text-xs opacity-60">
                {DEFAULT_SUBTITLE}
              </span>
            </span>
          </button>
          {models.map((model) => {
            const isSelected = value === model.id;
            const locked = !canChooseModel;
            return (
              <button
                key={model.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                aria-disabled={locked}
                disabled={locked}
                onClick={() => pick(model.id)}
                title={locked ? "Requires Subscription" : undefined}
                className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition ${
                  locked
                    ? "cursor-not-allowed opacity-40"
                    : `hover:bg-black hover:bg-opacity-5 ${isSelected ? "bg-black bg-opacity-5" : ""}`
                }`}
              >
                <span className="mt-0.5">
                  <ProviderMark provider={model.provider} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{model.label}</span>
                  {(locked || model.description) ? (
                    <span className="mt-0.5 block text-xs opacity-60">
                      {locked ? "Requires Subscription" : model.description}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PremiumModelPicker;
